import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { useToast } from '@/contexts/ToastContext';
import { formatINR, roundUp } from '@/utils/format';
import PaymentHeader from './components/PaymentHeader';
import InvoiceCard from './components/InvoiceCard';
import VoucherInfo from './components/VoucherInfo';
import PaymentAmount from './components/PaymentAmount';
import PaymentMethodSelector from './components/PaymentMethodSelector';
import TdsSection from './components/TdsSection';
import { apiClient } from '@/api/client';
import { useWarehouseStore } from '@/stores/warehouseStore';
import { bankReconApi, type CashBankLedger } from '@/api/bankReconApi';

// ─── Types ────────────────────────────────────────────────────────────────────

type PaymentMode = 'CASH' | 'UPI' | 'CARD' | 'CHEQUE' | 'NEFT' | 'RTGS';
type ChequeStatus = 'PENDING' | 'CLEARED' | 'BOUNCED' | 'INSUFFICIENT_BALANCE';

const SESSION_KEY = 'purchase_payment_state';

interface LocationState {
  invoiceId: string;
  invoiceNumber: string;
  supplierId: string;
  supplierName: string;
  invoiceAmount: number;
  balanceDue: number;
  invoiceDate: string;
  // TDS profile snapshotted on the invoice — informational fields that pre-fill
  // and drive the TDS section below, never re-derived from the client.
  tdsApplicable?: boolean;
  tdsSection?: string | null;
  tdsRate?: number | null;
  tdsAmount?: number;
  expectedPayment?: number;
  tdsPanAtInvoice?: string | null;
}

// Indian financial year runs April→March; quarters are Apr-Jun/Jul-Sep/Oct-Dec/Jan-Mar
// (not calendar quarters) — display only, the server recomputes and persists its own
// authoritative value on save.
function getFinancialYearAndQuarter(dateStr: string): { financialYear: string; quarter: string } {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return { financialYear: '', quarter: '' };
  const month = d.getMonth();
  const year = d.getFullYear();
  let fyStart: number;
  let quarter: string;
  if (month >= 3) {
    fyStart = year;
    quarter = month <= 5 ? 'Q1' : month <= 8 ? 'Q2' : 'Q3';
  } else {
    fyStart = year - 1;
    quarter = 'Q4';
  }
  return { financialYear: `${fyStart}-${String(fyStart + 1).slice(-2)}`, quarter };
}

/** Snapshot captured at save-time so print is never stale. */
interface SavedPaymentSnapshot {
  voucherNumber: string;
  date: string;
  paymentAmount: number;
  paymentMode: PaymentMode;
  referenceNo?: string;
  bankName?: string;
  chequeNo?: string;
  chequeDate?: string;
  notes?: string;
  supplierName: string;
  invoiceNumber: string;
  invoiceAmount: number;
}

// ─── API ──────────────────────────────────────────────────────────────────────

/**
 * POST /api/v1/purchase-payment
 *
 * Backend expects:  invoiceId, paymentAmount, paymentMode, date,
 *                   referenceNo?, cardLastFour?, bankName?, chequeNo?,
 *                   chequeDate?, chequeStatus?, bounceReason?, notes?
 *
 * Backend returns:  { success, message, data: { voucherNumber, ... } }
 */

async function apiCreatePayment(payload: {
  invoiceId: string;
  paymentAmount: number;
  paymentMode: PaymentMode;
  date: string;
  referenceNo?: string;
  cardLastFour?: string;
  bankName?: string;
  chequeNo?: string;
  chequeDate?: string;
  chequeStatus?: ChequeStatus;
  bounceReason?: string;
  notes?: string;
  warehouseId?: string;
  warehouseName?: string;
  isTdsDeducted?: boolean;
  tdsAmount?: number;
  certificateNo?: string;
  certificateDate?: string;
  cashBankAccountId?: string;
}): Promise<{ voucherNumber: string }> {
  const { data } = await apiClient.post('/api/v1/purchase-payment', payload);
  return data.data;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Resolve invoice state from navigation or sessionStorage (survives F5 refresh).
 * Called once synchronously before hooks so useState can use the value.
 */
function resolveState(locationState: unknown): LocationState | null {
  if (locationState) {
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(locationState));
    } catch {
      /* ignore */
    }
    return locationState as LocationState;
  }
  try {
    const saved = sessionStorage.getItem(SESSION_KEY);
    return saved ? (JSON.parse(saved) as LocationState) : null;
  } catch {
    return null;
  }
}

function clearSession() {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* ignore */
  }
}

// ─── Number to Words ──────────────────────────────────────────────────────────

export function numberToWords(n: number): string {
  // ✅ Round up first
  const num = Math.ceil(n);

  const ones = [
    '',
    'One',
    'Two',
    'Three',
    'Four',
    'Five',
    'Six',
    'Seven',
    'Eight',
    'Nine',
    'Ten',
    'Eleven',
    'Twelve',
    'Thirteen',
    'Fourteen',
    'Fifteen',
    'Sixteen',
    'Seventeen',
    'Eighteen',
    'Nineteen',
  ];
  const tens = [
    '',
    '',
    'Twenty',
    'Thirty',
    'Forty',
    'Fifty',
    'Sixty',
    'Seventy',
    'Eighty',
    'Ninety',
  ];

  if (num === 0) return 'Zero Rupees Only';

  const convert = (num: number): string => {
    if (num < 20) return ones[num];
    if (num < 100)
      return (
        tens[Math.floor(num / 10)] + (num % 10 ? ' ' + ones[num % 10] : '')
      );
    if (num < 1000)
      return (
        ones[Math.floor(num / 100)] +
        ' Hundred' +
        (num % 100 ? ' ' + convert(num % 100) : '')
      );
    if (num < 100000)
      return (
        convert(Math.floor(num / 1000)) +
        ' Thousand' +
        (num % 1000 ? ' ' + convert(num % 1000) : '')
      );
    if (num < 10000000)
      return (
        convert(Math.floor(num / 100000)) +
        ' Lakh' +
        (num % 100000 ? ' ' + convert(num % 100000) : '')
      );
    return (
      convert(Math.floor(num / 10000000)) +
      ' Crore' +
      (num % 10000000 ? ' ' + convert(num % 10000000) : '')
    );
  };

  return convert(num) + ' Rupees Only';
}

// ─── Print ────────────────────────────────────────────────────────────────────

function printVoucher(snap: SavedPaymentSnapshot) {
  // ✅ Round up amounts for print
  const roundedAmount = Math.ceil(snap.paymentAmount);
  const roundedInvoiceAmount = Math.ceil(snap.invoiceAmount);

  const html = `
    <html><head><title>Payment Voucher - ${snap.voucherNumber}</title>
    <style>
      body { font-family: Arial, sans-serif; padding: 40px; color: #1e293b; }
      .header { text-align: center; border-bottom: 2px solid #e2e8f0; padding-bottom: 16px; margin-bottom: 20px; }
      .company { font-size: 22px; font-weight: bold; }
      .title { font-size: 16px; color: #64748b; margin-top: 4px; }
      .row { display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px solid #f1f5f9; }
      .label { color: #64748b; font-size: 13px; }
      .value { font-weight: 600; font-size: 13px; }
      .amount-box { background: #f0fdf4; border: 2px solid #86efac; border-radius: 8px; padding: 16px; text-align: center; margin: 20px 0; }
      .amount-big { font-size: 28px; font-weight: bold; color: #16a34a; }
      .words { font-size: 12px; color: #64748b; margin-top: 4px; }
      .footer { margin-top: 40px; display: flex; justify-content: space-between; }
      .sign-box { text-align: center; border-top: 1px solid #1e293b; padding-top: 8px; width: 160px; font-size: 12px; color: #64748b; }
    </style></head><body>
    <div class="header">
      <div class="company">InvenPro</div>
      <div class="title">PAYMENT VOUCHER</div>
    </div>
    <div class="row"><span class="label">Voucher No</span><span class="value">${snap.voucherNumber}</span></div>
    <div class="row"><span class="label">Date</span><span class="value">${snap.date}</span></div>
    <div class="row"><span class="label">Supplier</span><span class="value">${snap.supplierName}</span></div>
    <div class="row"><span class="label">Invoice No</span><span class="value">${snap.invoiceNumber}</span></div>
    <div class="row"><span class="label">Invoice Amount</span><span class="value">${formatINR(roundedInvoiceAmount)}</span></div>
    <div class="row"><span class="label">Payment Mode</span><span class="value">${snap.paymentMode}</span></div>
    ${snap.referenceNo ? `<div class="row"><span class="label">Reference No</span><span class="value">${snap.referenceNo}</span></div>` : ''}
    ${snap.bankName && snap.chequeNo ? `<div class="row"><span class="label">Bank / Cheque</span><span class="value">${snap.bankName} · ${snap.chequeNo} · ${snap.chequeDate}</span></div>` : ''}
    <div class="amount-box">
      <div class="amount-big">${formatINR(roundedAmount)}</div>
      <div class="words">${numberToWords(roundedAmount)}</div>
    </div>
    ${snap.notes ? `<p style="font-size:12px;color:#64748b;">Notes: ${snap.notes}</p>` : ''}
    <div class="footer">
      <div class="sign-box">Prepared By</div>
      <div class="sign-box">Authorised Signatory</div>
    </div>
    </body></html>
  `;
  const w = window.open('', '_blank');
  if (w) {
    w.document.write(html);
    w.document.close();
    w.print();
  }
}

// ─── Success Modal ────────────────────────────────────────────────────────────

interface SuccessModalProps {
  voucherNumber: string;
  onClose: () => void;
  onPrint: () => void;
}

function SuccessModal({ voucherNumber, onClose, onPrint }: SuccessModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl p-8 w-full max-w-sm text-center">
        <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 mx-auto mb-4">
          <i className="ri-checkbox-circle-fill text-3xl text-green-600" />
        </div>
        <h3 className="text-lg font-bold text-[#1e293b] mb-1">
          Available Dues!
        </h3>
        <p className="text-sm text-slate-500 mb-1">
          Voucher No:{' '}
          <span className="font-semibold text-[#1e293b]">{voucherNumber}</span>
        </p>
        <p className="text-xs text-slate-400 mb-6">
          Invoice has been updated accordingly.
        </p>
        <div className="flex gap-3">
          <button
            onClick={onPrint}
            className="flex-1 flex items-center justify-center gap-2 h-10 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 cursor-pointer whitespace-nowrap transition-colors"
          >
            <i className="ri-printer-line" /> Print Voucher
            <kbd className="text-[10px] bg-white/20 px-1 rounded">F10</kbd>
          </button>
          <button
            onClick={onClose}
            className="flex-1 h-10 rounded-lg border border-[#e2e8f0] text-sm font-medium text-slate-600 hover:bg-slate-50 cursor-pointer whitespace-nowrap transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function PurchasePaymentPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const { selectedWarehouseId, selectedWarehouseName } = useWarehouseStore();

  // Resolve state synchronously before any hooks (handles both navigation and F5 refresh)
  const state = resolveState(location.state);

  // ── State ──────────────────────────────────────────────────────────────────
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  // TDS section — only offered when the invoice itself was flagged TDS-applicable.
  // Section/rate/PAN are read-only, sourced from that invoice snapshot.
  const tdsSectionAvailable = Boolean(state?.tdsApplicable);
  const [isTdsDeducted, setIsTdsDeducted] = useState(tdsSectionAvailable);
  const [tdsAmountInput, setTdsAmountInput] = useState<number | ''>(() =>
    tdsSectionAvailable
      ? Math.max(0, Math.min(state!.tdsAmount ?? 0, Number(state!.balanceDue || 0)))
      : 0,
  );
  const [certificateNo, setCertificateNo] = useState('');
  const [certificateDate, setCertificateDate] = useState('');
  const tdsAmountValue =
    tdsSectionAvailable && isTdsDeducted ? Number(tdsAmountInput) || 0 : 0;
  // ✅ Round up balance due when initializing — pre-fill cash amount = balance minus TDS
  const [paymentAmount, setPaymentAmount] = useState<number>(() => {
    const balanceDue = Number(state?.balanceDue || 0);
    return Math.max(0, Math.ceil(balanceDue - tdsAmountValue));
  });
  const [paymentMode, setPaymentMode] = useState<PaymentMode>('CASH');
  const [referenceNo, setReferenceNo] = useState('');
  const [cardLastFour, setCardLastFour] = useState('');
  const [bankName, setBankName] = useState('');
  const [chequeNo, setChequeNo] = useState('');
  const [chequeDate, setChequeDate] = useState('');
  const [chequeStatus, setChequeStatus] = useState<ChequeStatus>('PENDING');
  const [bounceReason, setBounceReason] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedSnapshot, setSavedSnapshot] =
    useState<SavedPaymentSnapshot | null>(null);
  const [cashBankAccountId, setCashBankAccountId] = useState<string>('');
  const [cashBankAccounts, setCashBankAccounts] = useState<CashBankLedger[]>([]);

  useEffect(() => {
    // Cash in hand + the company's bank accounts (Settings → Bank Accounts)
    bankReconApi.getCashBankLedgers()
      .then(setCashBankAccounts)
      .catch((err) => console.warn('Could not load cash/bank accounts:', err));
  }, []);

  const { financialYear, quarter } = getFinancialYearAndQuarter(date);
  const totalSettled = Number(paymentAmount || 0) + tdsAmountValue;
  const cashCap = Math.max(
    0,
    Math.ceil(Number(state?.balanceDue || 0)) - tdsAmountValue,
  );

  // ── Redirect if no invoice data ────────────────────────────────────────────
  useEffect(() => {
    if (!state) {
      toast.error(
        'No invoice data. Please use Record Payment from invoice list.',
      );
      navigate('/purchase/invoices');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!state) return null;

  // ── Derived flags ──────────────────────────────────────────────────────────
  const needsRef =
    paymentMode === 'UPI' || paymentMode === 'NEFT' || paymentMode === 'RTGS';
  const needsCheque = paymentMode === 'CHEQUE';
  const needsCard = paymentMode === 'CARD';
  const needsBounceReason =
    chequeStatus === 'BOUNCED' || chequeStatus === 'INSUFFICIENT_BALANCE';

  // ── Validation ─────────────────────────────────────────────────────────────
  const validate = (): string | null => {
    const amount = Number(paymentAmount);
    // ✅ Round up balance due for validation
    const roundedBalanceDue = Math.ceil(Number(state.balanceDue));

    if (amount <= 0 && !(tdsSectionAvailable && isTdsDeducted && tdsAmountValue > 0))
      return 'Payment amount must be greater than 0';

    // ✅ Minimum ₹1 validation
    if (amount > 0 && amount < 1) return 'Minimum payment amount is ₹1';

    if (tdsAmountValue < 0) return 'TDS amount cannot be negative';

    if (totalSettled > roundedBalanceDue + 1)
      return `Amount + TDS (₹${totalSettled}) cannot exceed balance due (₹${roundedBalanceDue})`;

    if (needsRef && !referenceNo.trim())
      return `Reference number is required for ${paymentMode}`;

    if (needsCheque) {
      if (!bankName.trim()) return 'Bank name is required';
      if (!chequeNo.trim()) return 'Cheque number is required';
      if (!chequeDate) return 'Cheque date is required';
      if (needsBounceReason && !bounceReason.trim())
        return 'Bounce reason is required';
    }
    return null;
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }

    setSaving(true);
    try {
      // ✅ Round up payment amount before sending
      const roundedPaymentAmount = Math.ceil(Number(paymentAmount));

      const payload = {
        invoiceId: state.invoiceId,
        paymentAmount: roundedPaymentAmount,
        paymentMode,
        date,
        referenceNo: referenceNo || undefined,
        cardLastFour: cardLastFour || undefined,
        bankName: bankName || undefined,
        chequeNo: chequeNo || undefined,
        chequeDate: chequeDate || undefined,
        chequeStatus: needsCheque ? chequeStatus : undefined,
        bounceReason: bounceReason || undefined,
        notes: notes || undefined,
        warehouseId:
          selectedWarehouseId && selectedWarehouseId !== 'ALL'
            ? selectedWarehouseId
            : undefined,
        warehouseName:
          selectedWarehouseId && selectedWarehouseId !== 'ALL'
            ? selectedWarehouseName
            : undefined,
        isTdsDeducted: tdsSectionAvailable && isTdsDeducted && tdsAmountValue > 0,
        tdsAmount: tdsAmountValue > 0 ? tdsAmountValue : undefined,
        certificateNo: certificateNo.trim() || undefined,
        certificateDate:
          certificateNo.trim() && certificateDate ? certificateDate : undefined,
        cashBankAccountId: cashBankAccountId || undefined,
      };

      console.log('📤 Payment Payload:', payload);

      const data = await apiCreatePayment(payload);

      clearSession();

      setSavedSnapshot({
        voucherNumber: data.voucherNumber,
        date,
        paymentAmount: roundedPaymentAmount,
        paymentMode,
        referenceNo: referenceNo || undefined,
        bankName: bankName || undefined,
        chequeNo: chequeNo || undefined,
        chequeDate: chequeDate || undefined,
        notes: notes || undefined,
        supplierName: state.supplierName,
        invoiceNumber: state.invoiceNumber,
        invoiceAmount: Math.ceil(Number(state.invoiceAmount)),
      });

      toast.success('Payment saved successfully!');
    } catch (error: unknown) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to save payment',
      );
    } finally {
      setSaving(false);
    }
  }, [
    state,
    date,
    paymentAmount,
    paymentMode,
    referenceNo,
    cardLastFour,
    bankName,
    chequeNo,
    chequeDate,
    chequeStatus,
    needsCheque,
    bounceReason,
    notes,
    toast,
    selectedWarehouseId,
    selectedWarehouseName,
    tdsSectionAvailable,
    isTdsDeducted,
    tdsAmountValue,
    certificateNo,
    certificateDate,
    totalSettled,
  ]);

  // ── Print Voucher Handler ────────────────────────────────────────────────────
  const handlePrintVoucher = useCallback(
    (voucherNumber: string) => {
      if (savedSnapshot) {
        printVoucher(savedSnapshot);
      } else {
        const tempSnapshot: SavedPaymentSnapshot = {
          voucherNumber: voucherNumber || '—',
          date,
          paymentAmount: Math.ceil(Number(paymentAmount)) || 0,
          paymentMode,
          referenceNo: referenceNo || undefined,
          bankName: bankName || undefined,
          chequeNo: chequeNo || undefined,
          chequeDate: chequeDate || undefined,
          notes: notes || undefined,
          supplierName: state.supplierName,
          invoiceNumber: state.invoiceNumber,
          invoiceAmount: Math.ceil(Number(state.invoiceAmount)),
        };
        printVoucher(tempSnapshot);
      }
    },
    [
      savedSnapshot,
      date,
      paymentAmount,
      paymentMode,
      referenceNo,
      bankName,
      chequeNo,
      chequeDate,
      notes,
      state,
    ],
  );

  // ── Keyboard shortcuts ─────────────────────────────────────────────────────
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'F9') {
        e.preventDefault();
        handleSave();
      }
      if (e.key === 'F10' && savedSnapshot) {
        e.preventDefault();
        printVoucher(savedSnapshot);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleSave, savedSnapshot]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <AppLayout>
      <div className="p-6 bg-[#f8fafc] min-h-full">
        <div className="max-w-2xl mx-auto">
          <PaymentHeader
            voucherNo={savedSnapshot?.voucherNumber ?? ''}
            onPrintVoucher={handlePrintVoucher}
          />

          <div className="space-y-4">
            <InvoiceCard state={state} />

            <VoucherInfo
              voucherNo={savedSnapshot?.voucherNumber ?? '—'}
              date={date}
              setDate={setDate}
              onPrintVoucher={handlePrintVoucher}
            />

            <PaymentAmount
              paymentAmount={paymentAmount}
              setPaymentAmount={setPaymentAmount}
              state={state}
              toast={toast}
              maxAmount={cashCap}
            />

            <TdsSection
              available={tdsSectionAvailable}
              isTdsDeducted={isTdsDeducted}
              setIsTdsDeducted={setIsTdsDeducted}
              tdsAmountInput={tdsAmountInput}
              setTdsAmountInput={setTdsAmountInput}
              tdsSection={state.tdsSection}
              tdsRate={state.tdsRate}
              tdsPanAtInvoice={state.tdsPanAtInvoice}
              financialYear={financialYear}
              quarter={quarter}
              certificateNo={certificateNo}
              setCertificateNo={setCertificateNo}
              certificateDate={certificateDate}
              setCertificateDate={setCertificateDate}
              tdsAmountValue={tdsAmountValue}
            />

            <PaymentMethodSelector
              paymentMode={paymentMode}
              setPaymentMode={setPaymentMode}
              needsRef={needsRef}
              needsCheque={needsCheque}
              needsCard={needsCard}
              needsBounceReason={needsBounceReason}
              referenceNo={referenceNo}
              setReferenceNo={setReferenceNo}
              cardLastFour={cardLastFour}
              setCardLastFour={setCardLastFour}
              bankName={bankName}
              setBankName={setBankName}
              chequeNo={chequeNo}
              setChequeNo={setChequeNo}
              chequeDate={chequeDate}
              setChequeDate={setChequeDate}
              chequeStatus={chequeStatus}
              setChequeStatus={setChequeStatus}
              bounceReason={bounceReason}
              setBounceReason={setBounceReason}
              cashBankAccountId={cashBankAccountId}
              setCashBankAccountId={setCashBankAccountId}
              cashBankAccounts={cashBankAccounts}
            />

            {/* Notes */}
            <div className="bg-white border border-[#e2e8f0] rounded-xl p-4">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-2">
                Notes (optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Any additional notes..."
                className="w-full px-3 py-2 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5] resize-none"
              />
            </div>

            {/* Payment Summary — only shown once TDS enters the picture */}
            {tdsAmountValue > 0 && (
              <div className="bg-white border border-[#e2e8f0] rounded-xl p-4">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">
                  Payment Summary
                </p>
                <div className="space-y-1.5">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Cash Paid</span>
                    <span className="font-semibold text-[#1e293b]">
                      {formatINR(Math.ceil(Number(paymentAmount || 0)))}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">TDS Deducted</span>
                    <span className="font-semibold text-[#1e293b]">
                      {formatINR(tdsAmountValue)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm pt-1.5 border-t border-[#e2e8f0]">
                    <span className="font-semibold text-[#1e293b]">Total Settled</span>
                    <span className="font-bold text-[#4f46e5]">
                      {formatINR(Math.ceil(totalSettled))}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Save Button */}
            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full h-12 flex items-center justify-center gap-2 rounded-xl bg-[#4f46e5] text-white text-sm font-bold hover:bg-indigo-700 disabled:opacity-60 cursor-pointer whitespace-nowrap transition-colors"
            >
              {saving ? (
                <>
                  <i className="ri-loader-4-line animate-spin" /> Saving...
                </>
              ) : (
                <>
                  <i className="ri-save-3-line" /> Save Payment{' '}
                  <kbd className="text-[10px] bg-white/20 px-1.5 rounded ml-1">
                    F9
                  </kbd>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Success modal — shown after API responds with real voucher number */}
      {savedSnapshot && (
        <SuccessModal
          voucherNumber={savedSnapshot.voucherNumber}
          onClose={() => {
            clearSession();
            navigate('/purchase/payments');
          }}
          onPrint={() => {
            printVoucher(savedSnapshot);
            clearSession();
            navigate('/purchase/payments');
          }}
        />
      )}
    </AppLayout>
  );
}
